import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import AvailabilityGrid from '../components/AvailabilityGrid';
import HongKongDistrictSelector from '../components/HongKongDistrictSelector';
import { api } from '../services/api';
import { CheckCircle, AlertCircle } from 'lucide-react';

const teachingStyles = [
  'Structured', 'Flexible', 'Practical', 'Fun / Games / Activities',
  'Story/Song/Video-based', 'Reading & Writing focus', 'Speaking & Pronunciation focus',
  'Exam-focused', 'Business-focused', 'Kids-friendly', 'Tech-assisted lessons'
];

const ageGroupOptions = ['adults'];

const classFormatOptions = [
  { value: '1-on-1', label: '1-on-1' },
  { value: 'small_group', label: 'Small Group (2–4)' },
  { value: 'large_group', label: 'Large Group (5+)' },
];

const parseAvailability = (raw) => {
  if (!raw) return {};
  if (typeof raw === 'object') return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return {};
  }
};

const SectionHeading = ({ children }) => (
  <h2 className="text-purple-300 font-semibold text-base mb-3 mt-6 border-b border-gray-800 pb-2">{children}</h2>
);

const CheckboxGroup = ({ options, selected, onChange, valueKey = null, labelKey = null }) => (
  <div className="flex flex-wrap gap-2">
    {options.map((opt) => {
      const value = valueKey ? opt[valueKey] : opt;
      const label = labelKey ? opt[labelKey] : opt;
      const checked = selected.includes(value);
      return (
        <label
          key={value}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm transition-colors ${
            checked
              ? 'bg-[#7e55f6] bg-opacity-20 border-[#7e55f6] text-white'
              : 'border-gray-700 text-gray-400 hover:border-gray-500'
          }`}
        >
          <input
            type="checkbox"
            className="sr-only"
            checked={checked}
            onChange={() => {
              if (checked) {
                onChange(selected.filter((v) => v !== value));
              } else {
                onChange([...selected, value]);
              }
            }}
          />
          {label}
        </label>
      );
    })}
  </div>
);

const TutorUpdatePage = () => {
  useDocumentTitle('Update Your Tutor Profile');

  const { tutorId } = useParams();
  const [tutor, setTutor] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState({
    availability: {},
    availabilityNotes: '',
    locationPreference: 'both',
    classFormats: [],
    baseDistricts: [],
    hourlyRateOnline: '',
    hourlyRateInPerson: '',
    teachingStyles: [],
    ageGroups: [],
  });

  useEffect(() => {
    if (!tutorId) {
      setLoadError('Invalid link. Please check the email you received.');
      setLoading(false);
      return;
    }
    api.tutors.getById(tutorId).then((data) => {
      if (!data) {
        setLoadError('Unable to load your profile. The link may have expired or be invalid.');
      } else {
        setTutor(data);
        setFormData({
          availability: parseAvailability(data.availability),
          availabilityNotes: data.availabilityNotes || '',
          locationPreference: data.locationPreference || 'both',
          classFormats: data.classFormats || [],
          baseDistricts: data.baseDistricts || [],
          hourlyRateOnline: data.hourlyRateOnline || '',
          hourlyRateInPerson: data.hourlyRateInPerson || '',
          teachingStyles: data.teachingStyles || [],
          ageGroups: data.ageGroups || [],
        });
      }
      setLoading(false);
    });
  }, [tutorId]);

  const handleDistrictsChange = useCallback((districts) => {
    setFormData((prev) => ({ ...prev, baseDistricts: districts }));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError(null);
    const response = await api.tutors.updateProfile(tutorId, formData);
    if (response.success) {
      setSuccess(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      setSubmitError(response.message || 'Failed to save. Please try again.');
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <svg className="animate-spin h-10 w-10 text-[#7e55f6]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-4">
        <div className="max-w-md mx-auto bg-gray-900 border border-gray-800 rounded-xl p-8 text-center">
          <AlertCircle size={40} className="text-red-400 mx-auto mb-4" />
          <p className="text-red-400">{loadError}</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-4">
        <div className="max-w-md mx-auto bg-gray-900 border border-gray-800 rounded-xl p-8 text-center">
          <CheckCircle size={48} className="text-green-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-white mb-3">Profile Updated!</h1>
          <p className="text-gray-400 text-sm">
            Thank you for keeping your availability current. We'll be in touch when we have matching students.
          </p>
        </div>
      </div>
    );
  }

  const showInPerson = formData.locationPreference !== 'online_only';

  return (
    <div className="min-h-screen bg-black text-white">
      <header className="py-6 px-4 border-b border-gray-800 text-center">
        <span className="text-white font-bold text-lg tracking-wide">Mandarin Tutor HK</span>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-white mb-1">Update Your Tutor Profile</h1>
        {tutor && (
          <p className="text-gray-400 text-sm mb-6">Hi {tutor.firstName}, please review and update your details below.</p>
        )}

        <form onSubmit={handleSubmit}>
          <SectionHeading>Weekly Availability</SectionHeading>
          <p className="text-gray-500 text-xs mb-3">Select all times you are generally available to teach.</p>
          <AvailabilityGrid
            availability={formData.availability}
            onChange={(val) => setFormData((prev) => ({ ...prev, availability: val }))}
            showHeatmap={false}
          />

          <SectionHeading>Availability Notes</SectionHeading>
          <textarea
            rows={3}
            value={formData.availabilityNotes}
            onChange={(e) => setFormData((prev) => ({ ...prev, availabilityNotes: e.target.value }))}
            placeholder="Any notes about your schedule, holidays, or exceptions…"
            className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-[#7e55f6] resize-none"
          />

          <SectionHeading>Location Preference</SectionHeading>
          <div className="flex flex-col gap-2">
            {[
              { value: 'online_only', label: 'Online only' },
              { value: 'in_person_only', label: 'In-person only' },
              { value: 'both', label: 'Both online and in-person' },
            ].map((opt) => (
              <label key={opt.value} className="flex items-center gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="locationPreference"
                  value={opt.value}
                  checked={formData.locationPreference === opt.value}
                  onChange={() => setFormData((prev) => ({ ...prev, locationPreference: opt.value }))}
                  className="accent-[#7e55f6]"
                />
                <span className="text-sm text-gray-300">{opt.label}</span>
              </label>
            ))}
          </div>

          <SectionHeading>Hourly Rates (HKD)</SectionHeading>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Online rate</label>
              <Input
                type="number"
                name="hourlyRateOnline"
                value={formData.hourlyRateOnline}
                onChange={(e) => setFormData((prev) => ({ ...prev, hourlyRateOnline: e.target.value }))}
                placeholder="e.g. 350"
              />
            </div>
            {showInPerson && (
              <div>
                <label className="block text-xs text-gray-500 mb-1">In-person rate</label>
                <Input
                  type="number"
                  name="hourlyRateInPerson"
                  value={formData.hourlyRateInPerson}
                  onChange={(e) => setFormData((prev) => ({ ...prev, hourlyRateInPerson: e.target.value }))}
                  placeholder="e.g. 400"
                />
              </div>
            )}
          </div>

          <SectionHeading>Class Formats</SectionHeading>
          <CheckboxGroup
            options={classFormatOptions}
            selected={formData.classFormats}
            onChange={(val) => setFormData((prev) => ({ ...prev, classFormats: val }))}
            valueKey="value"
            labelKey="label"
          />

          <SectionHeading>Teaching Styles</SectionHeading>
          <CheckboxGroup
            options={teachingStyles}
            selected={formData.teachingStyles}
            onChange={(val) => setFormData((prev) => ({ ...prev, teachingStyles: val }))}
          />

          <SectionHeading>Age Groups</SectionHeading>
          <CheckboxGroup
            options={[{ value: 'adults', label: '18+ (Adults)' }]}
            selected={formData.ageGroups}
            onChange={(val) => setFormData((prev) => ({ ...prev, ageGroups: val }))}
            valueKey="value"
            labelKey="label"
          />

          {showInPerson && (
            <>
              <SectionHeading>Teaching Locations (Districts)</SectionHeading>
              <p className="text-gray-500 text-xs mb-3">Select all Hong Kong districts where you can teach in person.</p>
              <HongKongDistrictSelector
                selectedDistricts={formData.baseDistricts}
                onChange={handleDistrictsChange}
              />
            </>
          )}

          {submitError && (
            <div className="mt-6 p-3 bg-red-900 bg-opacity-30 border border-red-700 rounded-lg text-red-400 text-sm">
              {submitError}
            </div>
          )}

          <div className="mt-8 mb-12">
            <Button
              type="submit"
              variant="primary"
              isLoading={submitting}
              disabled={submitting}
              className="w-full"
            >
              Save Changes
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default TutorUpdatePage;
